// src/services/holidayApi.ts
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
import { createAuthHeaders } from '../utils/apiHelpers';

// Holiday Types
export interface Holiday {
  holidayId: number;
  year: number;
  holidayDate: string; // YYYY-MM-DD format
  holidayName: string;
  description: string | null;
  isAutoAdded: boolean;
  createdBy: number | null;
  createdAt: string | null; // ISO 8601 timestamp
  updatedBy: number | null;
  updatedAt: string | null; // ISO 8601 timestamp
}

export interface HolidayFilters {
  year?: number;
  startDate?: string; // YYYY-MM-DD
  endDate?: string; // YYYY-MM-DD
}

export interface CreateHolidayRequest {
  year: number; // 1900-2100
  holidayDate: string; // YYYY-MM-DD format
  holidayName: string; // Required, max 255 characters
  description?: string | null; // Optional
  isAutoAdded?: boolean; // Optional, default: false
}

export interface UpdateHolidayRequest {
  year?: number; // 1900-2100
  holidayDate?: string; // YYYY-MM-DD format
  holidayName?: string; // Max 255 characters
  description?: string | null;
}

export interface InitializeYearRequest {
  year: number; // 1900-2100
}

export interface InitializeYearResponse {
  success: boolean;
  message: string;
  holidays: Holiday[];
}

// Error Type
export interface HolidayApiError {
  error: boolean;
  message: string;
  code?: 'AUTH_REQUIRED' | 'NOT_FOUND' | 'VALIDATION_ERROR' | 'NETWORK_ERROR' | 'DUPLICATE_HOLIDAY' | 'HOLIDAYS_ALREADY_EXIST';
  errors?: Array<{
    field: string;
    message: string;
  }>;
}

/**
 * Get holidays for a specific date range
 * Convenience function for date calculations
 */
export async function getHolidaysForDateRange(startDate: string, endDate: string): Promise<Holiday[]> {
  return getHolidays({ startDate, endDate });
}

/**
 * Get holidays with optional filters
 */
export async function getHolidays(filters?: HolidayFilters): Promise<Holiday[]> {
  const queryParams = new URLSearchParams();
  if (filters?.year) queryParams.append('year', filters.year.toString());
  if (filters?.startDate) queryParams.append('startDate', filters.startDate);
  if (filters?.endDate) queryParams.append('endDate', filters.endDate);

  // Try non-admin endpoint first (when backend implements it)
  let url = `${API_BASE_URL}/api/holidays${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
  
  let response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  // If non-admin endpoint doesn't exist (404), fall back to admin endpoint
  if (response.status === 404) {
    url = `${API_BASE_URL}/api/admin/holidays${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as HolidayApiError;
  }

  // For 403, return empty array instead of throwing - Airport Managers may not have access yet
  // Silently handle this - holidays are optional for date calculations
  if (response.status === 403) {
    return [];
  }

  if (!response.ok) {
    // For 404 on admin endpoint too, return empty array (endpoint may not exist)
    // Silently handle this - holidays are optional for date calculations
    if (response.status === 404) {
      return [];
    }
    const errorText = await response.text();
    throw {
      error: true,
      message: `Failed to fetch holidays: ${response.statusText}. ${errorText}`,
      code: 'NETWORK_ERROR',
    } as HolidayApiError;
  }

  return response.json();
}

/**
 * Get single holiday by ID
 */
export async function getHoliday(holidayId: number): Promise<Holiday> {
  // Use non-admin endpoint for GET requests - reading holidays should be available to all authenticated users
  const url = `${API_BASE_URL}/api/holidays/${holidayId}`;
  
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as HolidayApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Holiday not found', code: 'NOT_FOUND' } as HolidayApiError;
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw {
      error: true,
      message: `Failed to fetch holiday: ${response.statusText}. ${errorText}`,
      code: 'NETWORK_ERROR',
    } as HolidayApiError;
  }

  return response.json();
}

/**
 * Get list of years that have holidays defined
 */
export async function getHolidayYears(): Promise<number[]> {
  // Use non-admin endpoint for GET requests - reading holidays should be available to all authenticated users
  const url = `${API_BASE_URL}/api/holidays/years`;
  
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as HolidayApiError;
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw {
      error: true,
      message: `Failed to fetch holiday years: ${response.statusText}. ${errorText}`,
      code: 'NETWORK_ERROR',
    } as HolidayApiError;
  }

  return response.json();
}

/**
 * Create a new holiday
 */
export async function createHoliday(data: CreateHolidayRequest): Promise<Holiday> {
  const url = `${API_BASE_URL}/api/admin/holidays`;
  
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: createAuthHeaders(),
    body: JSON.stringify(data),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as HolidayApiError;
  }

  if (response.status === 403) {
    throw { error: true, message: 'Admin access required', code: 'AUTH_REQUIRED' } as HolidayApiError;
  }

  if (response.status === 400) {
    const error = await response.json();
    throw {
      error: true,
      message: error.message || 'Validation failed',
      code: error.code === 'DUPLICATE_HOLIDAY' ? 'DUPLICATE_HOLIDAY' : 'VALIDATION_ERROR',
      errors: error.errors,
    } as HolidayApiError;
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw {
      error: true,
      message: `Failed to create holiday: ${response.statusText}. ${errorText}`,
      code: 'NETWORK_ERROR',
    } as HolidayApiError;
  }

  return response.json();
}

/**
 * Update an existing holiday
 */
export async function updateHoliday(holidayId: number, data: UpdateHolidayRequest): Promise<Holiday> {
  const url = `${API_BASE_URL}/api/admin/holidays/${holidayId}`;
  
  const response = await fetch(url, {
    method: 'PUT',
    credentials: 'include',
    headers: createAuthHeaders(),
    body: JSON.stringify(data),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as HolidayApiError;
  }

  if (response.status === 403) {
    throw { error: true, message: 'Admin access required', code: 'AUTH_REQUIRED' } as HolidayApiError;
  }

  if (response.status === 400) {
    const error = await response.json();
    throw {
      error: true,
      message: error.message || 'Validation failed',
      code: error.code === 'DUPLICATE_HOLIDAY' ? 'DUPLICATE_HOLIDAY' : 'VALIDATION_ERROR',
      errors: error.errors,
    } as HolidayApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Holiday not found', code: 'NOT_FOUND' } as HolidayApiError;
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw {
      error: true,
      message: `Failed to update holiday: ${response.statusText}. ${errorText}`,
      code: 'NETWORK_ERROR',
    } as HolidayApiError;
  }

  return response.json();
}

/**
 * Delete a holiday
 */
export async function deleteHoliday(holidayId: number): Promise<{ success: boolean; message: string }> {
  const url = `${API_BASE_URL}/api/admin/holidays/${holidayId}`;
  
  const response = await fetch(url, {
    method: 'DELETE',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as HolidayApiError;
  }

  if (response.status === 403) {
    throw { error: true, message: 'Admin access required', code: 'AUTH_REQUIRED' } as HolidayApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Holiday not found', code: 'NOT_FOUND' } as HolidayApiError;
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw {
      error: true,
      message: `Failed to delete holiday: ${response.statusText}. ${errorText}`,
      code: 'NETWORK_ERROR',
    } as HolidayApiError;
  }

  // Handle 204 No Content (common for DELETE operations)
  if (response.status === 204) {
    return { success: true, message: 'Holiday deleted successfully' };
  }

  // Try to parse JSON response if available
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    return response.json();
  }

  // Default success response if no body
  return { success: true, message: 'Holiday deleted successfully' };
}

/**
 * Initialize standard holidays for a year
 * Creates New Year's Day (Jan 1), Independence Day (July 4), and Christmas Day (Dec 25)
 */
export async function initializeYear(year: number): Promise<Holiday[]> {
  const url = `${API_BASE_URL}/api/admin/holidays/initialize-year`;
  
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: createAuthHeaders(),
    body: JSON.stringify({ year }),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as HolidayApiError;
  }

  if (response.status === 403) {
    throw { error: true, message: 'Admin access required', code: 'AUTH_REQUIRED' } as HolidayApiError;
  }

  if (response.status === 400) {
    const error = await response.json();
    throw {
      error: true,
      message: error.message || 'Validation failed',
      code: error.code === 'HOLIDAYS_ALREADY_EXIST' ? 'HOLIDAYS_ALREADY_EXIST' : 'VALIDATION_ERROR',
      errors: error.errors,
    } as HolidayApiError;
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw {
      error: true,
      message: `Failed to initialize holidays: ${response.statusText}. ${errorText}`,
      code: 'NETWORK_ERROR',
    } as HolidayApiError;
  }

  const result: InitializeYearResponse = await response.json();
  return result.holidays;
}

